import { DOMParser as XMLDOMParser } from "@xmldom/xmldom";

type ListLevel = {
  format: string;
  text: string;
  start: number;
  left: number;
  hanging: number;
};

type ListItem = {
  label: string;
  left: number;
  hanging: number;
};

const SUPPORTED_NUMBER_FORMATS = new Set([
  "decimal",
  "upperRoman",
  "lowerRoman",
  "upperLetter",
  "lowerLetter",
]);

const childrenNamed = (element: Element, name: string): Element[] =>
  Array.from(element.childNodes).filter(
    (child): child is Element =>
      child.nodeType === 1 &&
      ((child as Element).localName ?? child.nodeName.split(":").pop()) === name
  );

const childNamed = (element: Element | undefined, name: string) =>
  element ? childrenNamed(element, name)[0] : undefined;

const elementsNamed = (element: Element | Document, name: string): Element[] =>
  Array.from(element.getElementsByTagName("*")).filter(
    (child) => (child.localName ?? child.nodeName.split(":").pop()) === name
  );

const attribute = (element: Element | undefined, name: string) =>
  element?.getAttribute(`w:${name}`) ??
  element?.getAttribute(name) ??
  Array.from(element?.attributes ?? []).find((item) => item.localName === name)?.value ??
  undefined;

const valueOf = (element: Element | undefined) =>
  attribute(element, "val");

const numberOf = (value: string | undefined, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const romanNumeral = (number: number) => {
  if (number <= 0 || number >= 4000) return String(number);
  const numerals: [number, string][] = [
    [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"],
    [100, "C"], [90, "XC"], [50, "L"], [40, "XL"],
    [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
  ];
  let remaining = number;
  let result = "";
  for (const [amount, numeral] of numerals) {
    while (remaining >= amount) {
      result += numeral;
      remaining -= amount;
    }
  }
  return result;
};

const alphabeticNumber = (number: number) => {
  if (number <= 0) return String(number);
  let remaining = number;
  let result = "";
  while (remaining > 0) {
    remaining--;
    result = String.fromCharCode(65 + (remaining % 26)) + result;
    remaining = Math.floor(remaining / 26);
  }
  return result;
};

const formatNumber = (number: number, format: string) => {
  switch (format) {
    case "upperRoman": return romanNumeral(number);
    case "lowerRoman": return romanNumeral(number).toLowerCase();
    case "upperLetter": return alphabeticNumber(number);
    case "lowerLetter": return alphabeticNumber(number).toLowerCase();
    default: return String(number);
  }
};

const points = (twips: number) => `${Math.max(0, twips / 20).toFixed(2)}pt`;

/**
 * Mammoth merges adjacent Word lists even when they use different numbering
 * instances or numeral formats. Replace those list markers with the labels
 * defined in numbering.xml before Quill turns them into one decimal list.
 */
export const useRestoreWordDocumentNumbering = (
  documentXml: string,
  numberingXml: string | undefined,
  html: string
): string => {
  if (!numberingXml || !html.includes("<li")) return html;

  const xmlParser = new XMLDOMParser();
  const documentXmlDom = xmlParser.parseFromString(documentXml, "text/xml");
  const numberingDom = xmlParser.parseFromString(numberingXml, "text/xml");
  const body = elementsNamed(documentXmlDom, "body")[0];
  if (!body) return html;

  const abstractLevels = new Map<string, Map<number, ListLevel>>();
  for (const abstract of elementsNamed(numberingDom, "abstractNum")) {
    const id = attribute(abstract, "abstractNumId");
    if (!id) continue;
    const levels = new Map<number, ListLevel>();
    for (const level of childrenNamed(abstract, "lvl")) {
      const index = numberOf(attribute(level, "ilvl"));
      const ind = childNamed(childNamed(level, "pPr"), "ind");
      levels.set(index, {
        format: valueOf(childNamed(level, "numFmt")) ?? "decimal",
        text: valueOf(childNamed(level, "lvlText")) ?? `%${index + 1}.`,
        start: numberOf(valueOf(childNamed(level, "start")), 1),
        left: numberOf(attribute(ind, "left")),
        hanging: numberOf(attribute(ind, "hanging")),
      });
    }
    abstractLevels.set(id, levels);
  }

  const numbering = new Map<string, { levels: Map<number, ListLevel>; starts: Map<number, number> }>();
  for (const instance of elementsNamed(numberingDom, "num")) {
    const id = attribute(instance, "numId");
    const abstractId = valueOf(childNamed(instance, "abstractNumId"));
    const levels = abstractId ? abstractLevels.get(abstractId) : undefined;
    if (!id || !levels) continue;
    const starts = new Map<number, number>();
    for (const override of childrenNamed(instance, "lvlOverride")) {
      const start = valueOf(childNamed(override, "startOverride"));
      if (start) starts.set(numberOf(attribute(override, "ilvl")), numberOf(start, 1));
    }
    numbering.set(id, { levels, starts });
  }

  const htmlDocument = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const container = htmlDocument.body.firstElementChild;
  if (!container) return html;
  const htmlBlocks = Array.from(container.querySelectorAll("p, h1, h2, h3, h4, h5, h6, li"));
  const wordParagraphs = elementsNamed(body, "p");
  // A mismatch means Mammoth omitted or merged a block. Avoid applying labels
  // to the wrong student text in that case.
  if (htmlBlocks.length !== wordParagraphs.length) return html;

  const counters = new Map<string, Map<number, number>>();
  const items = new Map<Element, ListItem>();
  for (let index = 0; index < wordParagraphs.length; index++) {
    const paragraph = wordParagraphs[index];
    const numPr = childNamed(childNamed(paragraph, "pPr"), "numPr");
    const numId = valueOf(childNamed(numPr, "numId"));
    const levelIndex = numberOf(valueOf(childNamed(numPr, "ilvl")));
    const instance = numId ? numbering.get(numId) : undefined;
    const level = instance?.levels.get(levelIndex);
    const block = htmlBlocks[index];
    if (
      !instance ||
      !level ||
      !SUPPORTED_NUMBER_FORMATS.has(level.format) ||
      block.tagName !== "LI"
    ) continue;

    const instanceCounters = counters.get(numId!) ?? new Map<number, number>();
    const current = (instanceCounters.get(levelIndex) ??
      (instance.starts.get(levelIndex) ?? level.start) - 1) + 1;
    instanceCounters.set(levelIndex, current);
    for (const nestedLevel of instanceCounters.keys()) {
      if (nestedLevel > levelIndex) instanceCounters.delete(nestedLevel);
    }
    counters.set(numId!, instanceCounters);

    const label = level.text.replace(/%(\d+)/g, (_, digit: string) => {
      const referencedIndex = Number(digit) - 1;
      const referencedLevel = instance.levels.get(referencedIndex);
      const referencedCounter = instanceCounters.get(referencedIndex);
      return referencedLevel && referencedCounter != undefined
        ? formatNumber(referencedCounter, referencedLevel.format)
        : "";
    });
    items.set(block, {
      label,
      left: level.left,
      hanging: level.hanging,
    });
  }

  if (items.size === 0) return html;

  const makeSpacer = (width: number) => {
    const span = htmlDocument.createElement("span");
    span.className = "tab-span";
    span.setAttribute("data-width", points(width));
    return span;
  };

  const flattenList = (list: Element): DocumentFragment | undefined => {
    const listItems = childrenNamed(list, "li");
    if (listItems.length === 0 || listItems.some((item) => !items.has(item))) {
      return undefined;
    }
    const fragment = htmlDocument.createDocumentFragment();
    for (const item of listItems) {
      const definition = items.get(item)!;
      const paragraph = htmlDocument.createElement("p");
      paragraph.style.cssText = (item as HTMLElement).style.cssText;
      paragraph.style.marginLeft = "";
      paragraph.style.textIndent = "";
      const nestedLists = Array.from(item.children).filter((child) =>
        ["OL", "UL"].includes(child.tagName)
      );
      for (const child of Array.from(item.childNodes)) {
        if (!nestedLists.includes(child as Element)) paragraph.appendChild(child);
      }
      const labelPosition = Math.max(0, definition.left - definition.hanging);
      // Word's hanging indent measures from the label to the body text. The
      // label width varies by font, so use a small fixed-width estimate.
      const estimatedLabelWidth = definition.label.length * 100 + 60;
      const labelGap = Math.max(100, definition.hanging - estimatedLabelWidth);
      paragraph.insertBefore(makeSpacer(labelGap), paragraph.firstChild);
      paragraph.insertBefore(htmlDocument.createTextNode(`${definition.label} `), paragraph.firstChild);
      if (labelPosition > 0) paragraph.insertBefore(makeSpacer(labelPosition), paragraph.firstChild);
      fragment.appendChild(paragraph);
      for (const nested of nestedLists) {
        const nestedFragment = flattenList(nested);
        if (nestedFragment) fragment.appendChild(nestedFragment);
      }
    }
    return fragment;
  };

  for (const list of Array.from(container.querySelectorAll("ol, ul"))) {
    if (!list.isConnected) continue;
    const fragment = flattenList(list);
    if (fragment) list.replaceWith(fragment);
  }

  return container.innerHTML;
};
